import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'

interface Modal {
  id: string
  component: string
  props?: Record<string, unknown>
}

interface UIState {
  // Theme
  theme: Theme
  effectiveTheme: 'light' | 'dark'
  
  // Sidebar
  isSidebarOpen: boolean
  isSidebarCollapsed: boolean
  
  // Modals
  modals: Modal[]
  
  // Tree visualization
  isTreeViewOpen: boolean
  treeZoom: number
  treePanPosition: { x: number; y: number }
  
  // Loading states
  globalLoading: boolean
  loadingMessage: string | null
  
  // Actions
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  
  openModal: (modal: Modal) => void
  closeModal: (id: string) => void
  closeAllModals: () => void
  
  setTreeViewOpen: (open: boolean) => void
  setTreeZoom: (zoom: number) => void
  setTreePanPosition: (position: { x: number; y: number }) => void
  
  setGlobalLoading: (loading: boolean, message?: string) => void
}

// Get effective theme based on system preference
const getEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

// Apply theme to document
const applyTheme = (effectiveTheme: 'light' | 'dark') => {
  if (effectiveTheme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: 'system',
      effectiveTheme: getEffectiveTheme('system'),
      
      isSidebarOpen: true,
      isSidebarCollapsed: false,
      
      modals: [],
      
      isTreeViewOpen: false,
      treeZoom: 1,
      treePanPosition: { x: 0, y: 0 },
      
      globalLoading: false,
      loadingMessage: null,
      
      // Actions
      setTheme: (theme) => {
        const effectiveTheme = getEffectiveTheme(theme)
        applyTheme(effectiveTheme)
        set({ theme, effectiveTheme })
      },
      
      toggleTheme: () => {
        const { theme } = get()
        const newTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
        const effectiveTheme = getEffectiveTheme(newTheme)
        applyTheme(effectiveTheme)
        set({ theme: newTheme, effectiveTheme })
      },
      
      toggleSidebar: () => set((state) => ({ 
        isSidebarOpen: !state.isSidebarOpen 
      })),
      
      setSidebarCollapsed: (collapsed) => set({ 
        isSidebarCollapsed: collapsed 
      }),
      
      openModal: (modal) => set((state) => ({ 
        modals: [...state.modals, modal] 
      })),
      
      closeModal: (id) => set((state) => ({ 
        modals: state.modals.filter((m) => m.id !== id) 
      })),
      
      closeAllModals: () => set({ modals: [] }),
      
      setTreeViewOpen: (open) => set({ isTreeViewOpen: open }),
      
      setTreeZoom: (zoom) => set({ treeZoom: zoom }),
      
      setTreePanPosition: (position) => set({ treePanPosition: position }),
      
      setGlobalLoading: (loading, message) => set({
        globalLoading: loading,
        loadingMessage: message ?? undefined,
      }),
    }),
    {
      name: 'aadya-ui',
      partialize: (state) => ({ 
        theme: state.theme,
        isSidebarCollapsed: state.isSidebarCollapsed,
      }),
    }
  )
)

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const { theme } = useUIStore.getState()
    if (theme === 'system') {
      const effectiveTheme = e.matches ? 'dark' : 'light'
      applyTheme(effectiveTheme)
      useUIStore.setState({ effectiveTheme })
    }
  })
}
